import { useEffect, useState, type CSSProperties, type KeyboardEvent } from "react";
import { ChevronLeft, ChevronRight, Loader2, Wrench } from "lucide-react";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ToolCallCard } from "./ToolCallCard";
import type { ToolCallEntry } from "./useTurns";

interface ToolCallsDisclosureProps {
  entries: ToolCallEntry[];
}

// Tool-call list for a turn. Starts closed so the chat stays compact when a
// turn kicks off; the user opens it themselves if they want to watch tool
// progress.
export const ToolCallsDisclosure = ({ entries }: ToolCallsDisclosureProps) => {
  const [open, setOpen] = useState(false);

  // Pager state. By default we follow the latest streamed call; once the user
  // navigates we flip into sticky "reading" mode and stop auto-advancing —
  // even if they happen to navigate back onto the latest call. Reading mode
  // resets each time the disclosure is re-opened.
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (!reading && entries.length > 0) {
      setSelectedIdx(entries.length - 1);
    }
  }, [entries.length, reading]);

  useEffect(() => {
    if (open) {
      setReading(false);
      setSelectedIdx(Math.max(0, entries.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snap to latest only on open transitions
  }, [open]);

  const move = (delta: number) => {
    const next = selectedIdx + delta;
    if (next < 0 || next >= entries.length) return;
    setSelectedIdx(next);
    setReading(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      move(1);
    } else if (e.key === "Home" && entries.length > 0) {
      e.preventDefault();
      setSelectedIdx(0);
      setReading(true);
    } else if (e.key === "End" && entries.length > 0) {
      e.preventDefault();
      setSelectedIdx(entries.length - 1);
      setReading(true);
    }
  };

  if (entries.length === 0) return null;

  const summary = entries.length === 1 ? "1 tool call" : `${entries.length} tool calls`;
  const current = entries[selectedIdx];
  const atFirst = selectedIdx === 0;
  const atLast = selectedIdx === entries.length - 1;
  const pending = current.result === undefined;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      <CollapsibleTrigger
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground",
          "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <ChevronRight
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
        <span>{summary}</span>
      </CollapsibleTrigger>
      <CollapsibleContent animated>
        <div
          role="group"
          aria-label="Tool calls"
          tabIndex={0}
          onKeyDown={onKeyDown}
          className={cn(
            "mt-2 overflow-hidden rounded-lg border border-border bg-background shadow-sm",
            "outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          )}
        >
          <div className="flex items-center gap-1 bg-inverse px-2 py-1.5 text-inverse-foreground">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => move(-1)}
                  disabled={atFirst}
                  aria-label="Previous tool call"
                  className={cn(
                    "inline-flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-md bg-inverse-foreground/5 px-1.5 text-xs font-medium text-inverse-foreground",
                    "hover:bg-inverse-foreground/20",
                    "disabled:pointer-events-none disabled:bg-transparent disabled:opacity-40",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inverse-foreground/40"
                  )}
                >
                  <ChevronLeft className="size-3.5" aria-hidden />
                  <span>Previous</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Previous tool call</p>
              </TooltipContent>
            </Tooltip>
            <div
              className="flex min-w-0 flex-1 items-center justify-center gap-2 text-xs"
              aria-live="polite"
              aria-atomic="true"
            >
              {pending ? (
                <Loader2
                  className="size-3.5 shrink-0 animate-spin text-inverse-muted-foreground"
                  aria-hidden
                />
              ) : (
                <Wrench className="size-3.5 shrink-0 text-inverse-muted-foreground" aria-hidden />
              )}
              {pending ? (
                <ShimmerText
                  className="truncate font-mono text-[11px] font-medium"
                  style={
                    {
                      "--base-color": "var(--inverse-muted-foreground)",
                      "--shimmer-color": "var(--inverse-foreground)",
                    } as CSSProperties
                  }
                >
                  {current.name}
                </ShimmerText>
              ) : (
                <span className="truncate font-mono text-[11px] font-medium text-inverse-foreground">
                  {current.name}
                </span>
              )}
              {entries.length > 1 && (
                <span className="shrink-0 text-[11px] tabular-nums text-inverse-muted-foreground">
                  {selectedIdx + 1} / {entries.length}
                </span>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => move(1)}
                  disabled={atLast}
                  aria-label="Next tool call"
                  className={cn(
                    "inline-flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-md bg-inverse-foreground/5 px-1.5 text-xs font-medium text-inverse-foreground",
                    "hover:bg-inverse-foreground/20",
                    "disabled:pointer-events-none disabled:bg-transparent disabled:opacity-40",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inverse-foreground/40"
                  )}
                >
                  <span>Next</span>
                  <ChevronRight className="size-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Next tool call</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <ToolCallCard key={current.id} entry={current} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
